// CSRF Token utility for secure external navigation
export class CSRFProtection {
    private static readonly TOKEN_KEY = 'worq_csrf_token';
    private static readonly TOKEN_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours

    /**
     * Generate a cryptographically secure CSRF token
     */
    static generateToken(): string {
        const array = new Uint8Array(32);
        crypto.getRandomValues(array);
        const token = Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');

        // Store token with expiry
        const expiry = Date.now() + this.TOKEN_EXPIRY;
        localStorage.setItem(this.TOKEN_KEY, JSON.stringify({ token, expiry }));

        return token;
    }

    /**
     * Validate and retrieve the current CSRF token
     */
    static getValidToken(): string | null {
        const stored = localStorage.getItem(this.TOKEN_KEY);
        if (!stored) return null;

        try {
            const { token, expiry } = JSON.parse(stored);
            if (Date.now() > expiry) {
                localStorage.removeItem(this.TOKEN_KEY);
                return null;
            }
            return token;
        } catch {
            localStorage.removeItem(this.TOKEN_KEY);
            return null;
        }
    }

    /**
     * Validate a provided token against the stored token
     */
    static validateToken(providedToken: string): boolean {
        const storedToken = this.getValidToken();
        return storedToken === providedToken;
    }

    /**
     * Clear the stored CSRF token
     */
    static clearToken(): void {
        localStorage.removeItem(this.TOKEN_KEY);
    }

    /**
     * Get CSRF headers for secure requests
     */
    static getHeaders(): Record<string, string> {
        const token = this.getValidToken() || this.generateToken();
        return {
            'X-CSRF-Token': token,
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'application/json',
        };
    }
}

/**
 * Secure navigation function with CSRF protection
 * This function ensures that external navigation is secure and authenticated
 */
export const secureNavigate = (url: string, target: '_blank' | '_self' = '_blank'): void => {
    // Generate a new CSRF token for this navigation
    const csrfToken = CSRFProtection.generateToken();

    // Create a secure navigation URL with CSRF token
    const secureUrl = new URL(url);
    secureUrl.searchParams.set('csrf_token', csrfToken);
    secureUrl.searchParams.set('timestamp', Date.now().toString());

    // Add additional security parameters
    secureUrl.searchParams.set('source', 'worq_public_site');
    secureUrl.searchParams.set('version', '1.0');

    // Open the secure URL with proper security attributes
    const newWindow = window.open(secureUrl.toString(), target, 'noopener,noreferrer');

    if (newWindow) {
        // Set up security monitoring
        newWindow.addEventListener('beforeunload', () => {
            // Clear the CSRF token when the external window is closed
            CSRFProtection.clearToken();
        });

        // Add security headers to the new window if possible
        try {
            newWindow.document.addEventListener('DOMContentLoaded', () => {
                // This is a security measure to ensure the external site loads properly
                if (process.env.NODE_ENV === 'development') {
                    console.log('Secure navigation initiated to:', secureUrl.toString());
                }
            });
        } catch (error) {
            // Cross-origin restrictions may prevent this, which is expected
            if (process.env.NODE_ENV === 'development') {
                console.log('Secure navigation to external site initiated');
            }
        }
    }
};

/**
 * Validate incoming CSRF tokens from external sources
 * This should be called when receiving data back from external sites
 */
export const validateExternalToken = (token: string, timestamp: string): boolean => {
    const currentTime = Date.now();
    const requestTime = parseInt(timestamp, 10);

    // Check if the request is within a reasonable time window (5 minutes)
    if (currentTime - requestTime > 5 * 60 * 1000) {
        return false;
    }

    return CSRFProtection.validateToken(token);
};
